import { useEffect, useRef } from 'react';
import { useAnalytics } from 'utils/analytics';
import { useLQDPath } from 'utils/paths';

/* useDetectWindowFocus sends an analytics event when the window gains or loses focus */
export const useDetectWindowFocus = () => {
  const { isIndependentLearning } = useLQDPath();
  const hasSentFocusEvent = useRef(false);
  const blurStartTime = useRef(0);

  const sendEvent = useAnalytics();
  useEffect(() => {
    const onFocus = () => {
      // only send one event if we're in independent learning
      if (isIndependentLearning && hasSentFocusEvent.current) {
        return;
      }
      if (blurStartTime.current) {
        const blurDuration = (Date.now() - blurStartTime.current) / 1000; // duration in seconds
        sendEvent({
          action: `Question lost and regained focus${isIndependentLearning ? ' - IL' : ''}`,
          category: 'window focus',
          ...(!isIndependentLearning && { labels: { duration: blurDuration } }), // don't send duration for independent learning
        });
        hasSentFocusEvent.current = true;
      }
    };

    const onBlur = () => {
      blurStartTime.current = Date.now();
    };

    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);

    return () => {
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);
    };
  }, [isIndependentLearning, sendEvent]);
};

/* useDetectGauthMathExtension sends an analytics event when the gauthmath extension gains focus. Limited to one event per render of a question. */
export const useDetectGauthMathExtension = () => {
  const { isIndependentLearning } = useLQDPath();

  const extensionElement = document.getElementById('gauthmath');

  const hasSentGauthMathExtensionEvent = useRef(false);
  const sendEvent = useAnalytics();

  useEffect(() => {
    if (!extensionElement || isIndependentLearning) {
      return;
    }

    const onGMExClick = () => {
      if (hasSentGauthMathExtensionEvent.current) {
        return;
      }
      sendEvent({
        action: 'gmx used on question page',
        category: 'window focus',
      });
      hasSentGauthMathExtensionEvent.current = true;
    };

    extensionElement?.addEventListener('focus', onGMExClick);

    return () => {
      extensionElement?.removeEventListener('focus', onGMExClick);
    };
  }, [extensionElement, isIndependentLearning, sendEvent]);
};
